'use client';

import { memo, useEffect, useMemo, useState } from 'react';
import { Tabs } from '@okee-uikit/react';
import cs from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import FilterLoading from 'public/filter.svg';

import s from './index.module.scss';

import { Image } from '#/components/custom-image-loader';
import { ClickEvent } from '#/components/event-tracker/client';
import {
  APP_NAME_SEARCH_KEY,
  Category,
  CATEGORY_SEARCH_KEY,
  CATEGORY_SEARCH_KEY_DELIMITER,
  type CategoryStrType,
} from '#/utils/const';
import { appInQuery, categoryIdInFilter, getProdCanonicalUrl } from '#/utils/funcs';
import type { AppSchema } from '#/utils/http/types';

const Filters = dynamic(() => import('#/components/filters').then((mod) => mod.Filters), {
  ssr: false, // multi select component had hydration errors and doesn't have correct option component
  loading: () => <Image alt='' src={FilterLoading} />,
});

function HighlightAppSection({ appsList, titleKey }: { appsList: AppSchema[]; titleKey: string }) {
  const t = useTranslations();
  if (appsList.length < 1) return null;
  return (
    <div className={s.section}>
      <h2>{t(titleKey)}</h2>
      <div className={s.list}>
        {appsList.map((app) => (
          <Link
            className={cs(s.cardWrapper, { [s.testApp]: app.isTestApp })}
            href={app.isTestApp ? `/app-center/test/detail/${app.id}` : getProdCanonicalUrl(app)}
            key={app.id}
            prefetch
          >
            <ClickEvent
              eventName='home_page_app_click'
              extraParams={{
                app_id: app.id,
                app_name: app.name,
                app_type: app.isTestApp ? '2' : '1',
              }}
            >
              <div className={s.highlightCard}>
                <div className={s.image}>
                  <Image alt={app.name} sizes='100%' src={app.appImageUrl} fill />
                </div>
                <div className={s.title}>
                  <h3 title={app.name}>{app.name}</h3>
                  <p title={app.blurb ?? app.description}>{app.blurb ?? app.description}</p>
                </div>
              </div>
            </ClickEvent>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CategoryAppSection({
  appsList,
  categoryId,
}: {
  appsList: AppSchema[];
  categoryId: number;
}) {
  const t = useTranslations();
  return (
    <div className={s.section}>
      <h2>{t(Category[categoryId].toLocaleLowerCase())}</h2>
      <div className={s.list}>
        {appsList.map((app) => (
          <Link
            className={cs(s.cardWrapper, { [s.testApp]: app.isTestApp })}
            href={app.isTestApp ? `/app-center/test/detail/${app.id}` : getProdCanonicalUrl(app)}
            key={app.id}
            prefetch
          >
            <ClickEvent
              eventName='home_page_app_click'
              extraParams={{
                app_id: app.id,
                app_name: app.name,
                app_type: app.isTestApp ? '2' : '1',
              }}
            >
              <div className={s.card}>
                <div className={s.image}>
                  {app.iconImageUrl && (
                    <Image alt={app.name} sizes='40px' src={app.iconImageUrl} fill />
                  )}
                </div>
                <div className={s.title}>
                  <h3 title={app.name}>{app.name}</h3>
                  <p title={app.blurb ?? app.description}>{app.blurb ?? app.description}</p>
                </div>
              </div>
            </ClickEvent>
          </Link>
        ))}
      </div>
    </div>
  );
}

const Content = memo(function Content({
  allApps,
  spotlightApps,
  installedApps,
}: {
  allApps: AppSchema[];
  spotlightApps: AppSchema[];
  installedApps: AppSchema[];
}) {
  const t = useTranslations();
  const searchParams = useSearchParams();

  // filter by name
  const v = searchParams?.get(APP_NAME_SEARCH_KEY);
  const nameFilteredSpotlightApps = useMemo(
    () => (v ? spotlightApps.filter((app) => appInQuery(app.name, v)) : spotlightApps),
    [v, spotlightApps]
  );
  const nameFilteredInstalledApps = useMemo(
    () => (v ? installedApps.filter((app) => appInQuery(app.name, v)) : installedApps),
    [v, installedApps]
  );

  const showInstalledAppsTab = nameFilteredInstalledApps.length > 0;
  // use controlled tabs to prevent new tab to become active
  const [tabId, setTabId] = useState('0');
  useEffect(() => {
    if (!showInstalledAppsTab) {
      setTabId('0');
    }
  }, [showInstalledAppsTab]);

  const nameFilteredApps = useMemo(() => {
    const spotlightIdSet = new Set(nameFilteredSpotlightApps.map((app) => app.id));
    return (v ? allApps.filter((app) => appInQuery(app.name, v)) : allApps).filter(
      (app) => !spotlightIdSet.has(app.id)
    ); // filter out spotlight apps to avoid displaying duplicate icons on home page;
  }, [v, allApps, nameFilteredSpotlightApps]);

  // get the list of categories
  const allCategoryStrings = useMemo(() => {
    if (!Array.isArray(nameFilteredApps)) return [];
    const categoryIds = nameFilteredApps.map((app) => app.categoryId);
    const local = new Set<CategoryStrType>();
    categoryIds.forEach((categoryId) => {
      if (categoryId in Category) {
        local.add(Category[categoryId] as CategoryStrType);
      }
    });

    return Array.from(local);
  }, [nameFilteredApps]);

  // filter by category
  const sortedFilteredCategoryIds = (
    searchParams?.get(CATEGORY_SEARCH_KEY)?.split(CATEGORY_SEARCH_KEY_DELIMITER) ||
    allCategoryStrings.map((categoryStr) => Category[categoryStr])
  )
    .map(Number)
    .filter(Boolean)
    .sort((a, b) => a - b);
  const sortedFilteredApps = nameFilteredApps
    .reduce(
      (acc, cur) => {
        if (!categoryIdInFilter(sortedFilteredCategoryIds, cur.categoryId)) {
          return acc;
        }
        if (!acc[cur.categoryId]) {
          acc[cur.categoryId] = [];
        }
        acc[cur.categoryId].push(cur);
        return acc;
      },
      Array.from<AppSchema[]>({ length: 0 })
    )
    .filter(Boolean);

  return (
    <Tabs
      activeId={tabId}
      barClassName={s.header}
      className={s.content}
      extraTabBar={
        <div className={s.search}>
          <Filters allCategoryStrings={allCategoryStrings} />
        </div>
      }
      tabPosition='top'
      type='basic'
      controlled
      onChange={setTabId}
    >
      <Tabs.Item id='0' tab={t('all_apps')}>
        <HighlightAppSection appsList={nameFilteredSpotlightApps} titleKey='spotlight_apps' />
        {sortedFilteredApps.map((appGroup, index) => (
          <CategoryAppSection
            appsList={appGroup}
            categoryId={sortedFilteredCategoryIds[index] || index}
            key={sortedFilteredCategoryIds[index] || index}
          />
        ))}
      </Tabs.Item>
      {showInstalledAppsTab && (
        <Tabs.Item id='1' tab={t('installed_apps')}>
          <HighlightAppSection appsList={nameFilteredInstalledApps} titleKey='installed_apps' />
        </Tabs.Item>
      )}
    </Tabs>
  );
});
export { Content };
